import { useState, useEffect } from "react";
import { getUsers, getAllSalons, assignSalonsToUser, bulkAssignSalons, updateUserRole, deleteUser } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  Users as UsersIcon, 
  Store, 
  Shield, 
  User, 
  Check, 
  MoreVertical,
  Trash2,
  ArrowUpCircle,
  ArrowDownCircle,
  CheckSquare
} from "lucide-react";
import { Navigate } from "react-router-dom";

const Users = () => {
  const { isAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Single user assignment
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSalons, setSelectedSalons] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  
  // Bulk assignment
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkSalons, setBulkSalons] = useState([]);
  
  // Delete confirmation
  const [deleteUserId, setDeleteUserId] = useState(null);
  
  // Role change confirmation
  const [roleChangeData, setRoleChangeData] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, salonsRes] = await Promise.all([
        getUsers(),
        getAllSalons()
      ]);
      setUsers(usersRes.data);
      setSalons(salonsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Single user assignment
  const openAssignDialog = (user) => {
    setSelectedUser(user);
    setSelectedSalons(user.assigned_salons || []);
  };

  const handleSalonToggle = (salonId) => {
    setSelectedSalons(prev => 
      prev.includes(salonId) 
        ? prev.filter(id => id !== salonId)
        : [...prev, salonId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!selectedUser) return;
    
    setSaveLoading(true);
    try {
      await assignSalonsToUser(selectedUser.id, selectedSalons);
      toast.success("Salons assigned successfully");
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign salons");
    } finally {
      setSaveLoading(false);
    }
  };

  // Bulk assignment
  const handleUserSelect = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllStaff = () => {
    const staffIds = users.filter(u => u.role === "staff").map(u => u.id);
    if (selectedUsers.length === staffIds.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(staffIds);
    }
  };

  const handleBulkSalonToggle = (salonId) => {
    setBulkSalons(prev =>
      prev.includes(salonId)
        ? prev.filter(id => id !== salonId)
        : [...prev, salonId]
    );
  };

  const handleBulkAssign = async () => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }
    
    setSaveLoading(true);
    try {
      await bulkAssignSalons(selectedUsers, bulkSalons);
      toast.success(`Salons assigned to ${selectedUsers.length} user(s)`);
      setShowBulkAssign(false);
      setBulkSalons([]);
      setSelectedUsers([]);
      setBulkMode(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign salons");
    } finally {
      setSaveLoading(false);
    }
  };

  // Role change
  const handleRoleChange = async () => {
    if (!roleChangeData) return;
    
    setSaveLoading(true);
    try {
      await updateUserRole(roleChangeData.userId, roleChangeData.newRole);
      toast.success(`User ${roleChangeData.newRole === "admin" ? "promoted to admin" : "demoted to staff"}`);
      setRoleChangeData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    
    setSaveLoading(true);
    try {
      await deleteUser(deleteUserId);
      toast.success("User deleted successfully");
      setDeleteUserId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setSaveLoading(false);
    }
  };

  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const getSalonNames = (salonIds) => {
    if (!salonIds || salonIds.length === 0) return "None";
    const names = salonIds
      .map(id => salons.find(s => s.id === id)?.name || "Unknown")
      .slice(0, 2);
    const remaining = salonIds.length - 2;
    return remaining > 0 ? `${names.join(", ")} +${remaining} more` : names.join(", ");
  };

  const staffUsers = users.filter(u => u.role === "staff");

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">User Management</h1>
          <p className="text-muted-foreground mt-1">Manage users, roles, and salon assignments</p>
        </div>
        <div className="flex gap-2">
          {bulkMode ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => { setBulkMode(false); setSelectedUsers([]); }}
                className="rounded-full"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setShowBulkAssign(true)}
                disabled={selectedUsers.length === 0}
                className="rounded-full bg-orange-500 hover:bg-orange-600"
                data-testid="bulk-assign-btn"
              >
                <Store className="w-4 h-4 mr-2" />
                Assign to {selectedUsers.length} User(s)
              </Button>
            </>
          ) : (
            <Button 
              variant="outline"
              onClick={() => setBulkMode(true)}
              className="rounded-full"
              disabled={staffUsers.length === 0}
              data-testid="enable-bulk-mode"
            >
              <CheckSquare className="w-4 h-4 mr-2" />
              Bulk Assign
            </Button>
          )}
        </div>
      </div>

      {/* Bulk mode select all */}
      {bulkMode && staffUsers.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={selectedUsers.length === staffUsers.length && staffUsers.length > 0}
                onCheckedChange={handleSelectAllStaff}
                data-testid="select-all-staff"
              />
              <span className="text-sm text-orange-800">
                Select all staff users ({staffUsers.length})
              </span>
            </div>
            <span className="text-sm text-orange-600 font-medium">
              {selectedUsers.length} selected
            </span>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {users.map((user) => (
            <Card key={user.id} className={`border-border/50 shadow-sm transition-all ${
              bulkMode && selectedUsers.includes(user.id) ? "ring-2 ring-orange-500" : ""
            }`}>
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    {/* Bulk selection checkbox */}
                    {bulkMode && user.role === "staff" && (
                      <Checkbox 
                        checked={selectedUsers.includes(user.id)}
                        onCheckedChange={() => handleUserSelect(user.id)}
                        data-testid={`select-user-${user.id}`}
                      />
                    )}
                    
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      user.role === "admin" ? "bg-orange-100" : "bg-primary/10"
                    }`}>
                      {user.role === "admin" ? (
                        <Shield className="w-6 h-6 text-orange-600" />
                      ) : (
                        <User className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-semibold text-lg text-slate-900">{user.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.role === "admin" 
                            ? "bg-orange-100 text-orange-700" 
                            : "bg-slate-100 text-slate-600"
                        }`}>
                          {user.role}
                        </span>
                        {user.id === currentUser?.id && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                      {user.role !== "admin" && (
                        <div className="flex items-center gap-1 mt-1 text-sm">
                          <Store className="w-3 h-3 text-muted-foreground" />
                          <span className="text-muted-foreground">
                            Assigned: {getSalonNames(user.assigned_salons)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {!bulkMode && (
                    <div className="flex items-center gap-2">
                      {user.role !== "admin" && (
                        <Button 
                          variant="outline" 
                          onClick={() => openAssignDialog(user)}
                          className="rounded-full"
                          data-testid={`assign-salons-${user.id}`}
                        >
                          <Store className="w-4 h-4 mr-2" />
                          Assign Salons
                        </Button>
                      )}
                      
                      {/* Actions dropdown */}
                      {user.id !== currentUser?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {user.role === "staff" ? (
                              <DropdownMenuItem 
                                onClick={() => setRoleChangeData({ userId: user.id, newRole: "admin", userName: user.name })}
                                data-testid={`promote-user-${user.id}`}
                              >
                                <ArrowUpCircle className="w-4 h-4 mr-2 text-orange-600" />
                                Promote to Admin
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem 
                                onClick={() => setRoleChangeData({ userId: user.id, newRole: "staff", userName: user.name })}
                                data-testid={`demote-user-${user.id}`}
                              >
                                <ArrowDownCircle className="w-4 h-4 mr-2 text-blue-600" />
                                Demote to Staff
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => setDeleteUserId(user.id)}
                              className="text-red-600 focus:text-red-600"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete User
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Single User Assign Salons Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Salons</DialogTitle>
            <DialogDescription>
              Select salons to assign to <strong>{selectedUser?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-[300px] overflow-y-auto">
            {salons.length > 0 ? (
              salons.map((salon) => (
                <div 
                  key={salon.id} 
                  className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => handleSalonToggle(salon.id)}
                >
                  <Checkbox 
                    checked={selectedSalons.includes(salon.id)}
                    onCheckedChange={() => handleSalonToggle(salon.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{salon.name}</p>
                  </div>
                  {selectedSalons.includes(salon.id) && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">No salons available</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedUser(null)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAssignments} 
              disabled={saveLoading}
              className="rounded-full bg-orange-500 hover:bg-orange-600"
              data-testid="save-assignments-btn"
            >
              {saveLoading ? "Saving..." : "Save Assignments"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssign} onOpenChange={setShowBulkAssign}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Bulk Assign Salons</DialogTitle>
            <DialogDescription>
              Select salons to assign to <strong>{selectedUsers.length} user(s)</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-[300px] overflow-y-auto">
            {salons.length > 0 ? (
              salons.map((salon) => (
                <div 
                  key={salon.id} 
                  className="flex items-center gap-3 p-3 rounded-lg border border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
                  onClick={() => handleBulkSalonToggle(salon.id)}
                >
                  <Checkbox 
                    checked={bulkSalons.includes(salon.id)}
                    onCheckedChange={() => handleBulkSalonToggle(salon.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{salon.name}</p>
                  </div>
                  {bulkSalons.includes(salon.id) && (
                    <Check className="w-4 h-4 text-primary" />
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-muted-foreground py-4">No salons available</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkAssign(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAssign} 
              disabled={saveLoading}
              className="rounded-full bg-orange-500 hover:bg-orange-600"
              data-testid="save-bulk-assignments-btn"
            >
              {saveLoading ? "Saving..." : `Assign to ${selectedUsers.length} User(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Confirmation */}
      <AlertDialog open={!!roleChangeData} onOpenChange={() => setRoleChangeData(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleChangeData?.newRole === "admin" ? "Promote to Admin" : "Demote to Staff"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleChangeData?.newRole === "admin" 
                ? `Are you sure you want to promote ${roleChangeData?.userName} to admin? They will have full access to all salons and user management.`
                : `Are you sure you want to demote ${roleChangeData?.userName} to staff? They will lose admin privileges and need salon assignments.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleRoleChange}
              className={roleChangeData?.newRole === "admin" ? "bg-orange-500 hover:bg-orange-600" : ""}
              data-testid="confirm-role-change"
            >
              {roleChangeData?.newRole === "admin" ? "Promote" : "Demote"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
